import {faGalaxy} from "@fortawesome/pro-solid-svg-icons/faGalaxy";
import {
    archiveMessages,
    deleteMessages,
    FilterableMessageCentreUrlSlugs,
    GetItemCounts,
    MessageCentreFolderNameMap,
    MessageCentreNames,
    MessageCentreUrlSlugs, MessageItemUpdateType,
    PaginatedItemCountsType, PrivateMessageCentreUrlSlugs,
    useInfiniteMessageCentre, useInfiniteMessageCentreResults, useUpdateMessageCentre
} from "@twigsby/client-data";
import {ColourSolid, useComboBoxSimple, useLayout, useNotifications} from "@twigsby/frontend-framework";
import React, {PropsWithChildren, useContext, useEffect, useMemo, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {FilterMessagesModal} from "../Components/MessageCentre/Modals/FilterMessagesModal";
import {ManualEventOnly} from "../Events";
import {
    MessageCentreFilterUrlMapping,
    MessageCentreUrlMapping,
    MessageCentreUrlParamsType
} from "../Helpers/MessageCentreMapping";
import {DeleteFilter, UpdateAllFilters} from "../Helpers/UrlSlugsAndFilters";
import useMessageItemActions from "../Hooks/useMessageItemActions";
import {
    MessageCentreContextType,
    MessageCentreProviderType, MessageUpdateStatusType
} from "../Types/MessageCentreProviderTypes";

const MessageCentreContext = React.createContext<MessageCentreContextType>({} as MessageCentreContextType);

const MessageCentreProvider = (props: PropsWithChildren<MessageCentreProviderType>) => {
    const params = useParams();
    const {messageId} = params;
    const location = useLocation();
    const navigate = useNavigate();
    const { addNotification } = useNotifications();
    const { addGlobalDocumentValue } = useLayout();


    const [folderName] = useState<string>(MessageCentreFolderNameMap['all']);
    const [folderMappedId] = useState<number>(MessageCentreNames[folderName]);

    const {
        allSelected,
        visibleSelected,
        visibleIndeterminate,
        selectedItems,
        selectedItemsRef,
        ToggleAll,
        ToggleVisible,
        ToggleSelected,
        ResetSelection,
    } = useComboBoxSimple();

    const [showFilterModal, setShowFilterModal] = useState<boolean>(false);

    const [urlParams, setUrlParams] = useState<MessageCentreUrlParamsType>(params);

    const [userFilters, setUserFilters ] = useState<FilterableMessageCentreUrlSlugs>(MessageCentreFilterUrlMapping(urlParams, location));
    const [privateFilters, setPrivateFilters ] = useState<PrivateMessageCentreUrlSlugs>(MessageCentreUrlMapping(urlParams, location));
    const [parsedFilters, setParsedFilters ] = useState<MessageCentreUrlSlugs>({...privateFilters, ...userFilters});

    const [messageCentreItemCounts, setMessageCentreItemCounts] = useState<PaginatedItemCountsType>({ loaded: 0, total: 0 } );
    const [messageResultsItemCounts, setMessageResultsItemCounts] = useState<PaginatedItemCountsType>({ loaded: 0, total: 0 } );

    const [messageUpdateStatus, setMessageUpdateStatus ] = useState<MessageUpdateStatusType>({ updating: false });

    const [firstResultThreadId, setFirstResultThreadId] = useState<number>(-1);
    const [ messageThreadOpen, setMessageThreadOpen ] = useState<boolean>(false);

    const {
        data: messageCentreData,
        error: messageCentreError,
        isError: messageCentreIsError,
        isLoading: messageCentreIsLoading,
        isSuccess: messageCentreIsSuccess,
        isFetchingNextPage: messageCentreIsFetchingNextPage,
        fetchNextPage: messageCentreFetchNextPage,
        hasNextPage: messageCentreHasNextPage,
        dataUpdatedAt: messageCentreDataUpdatedAt
    } = useInfiniteMessageCentre(folderMappedId);

    const {
        data: messageResultsData,
        error: messageResultsError,
        isError: messageResultsIsError,
        isLoading: messageResultsIsLoading,
        isSuccess: messageResultsIsSuccess,
        isFetchingNextPage: messageResultsIsFetchingNextPage,
        fetchNextPage: messageResultsFetchNextPage,
        hasNextPage: messageResultsHasNextPage,
        dataUpdatedAt: messageResultsDataUpdatedAt
    } = useInfiniteMessageCentreResults(parsedFilters);

    const { selectionActions: singleActions, FilterActions: FilterSingleActions } = useMessageItemActions();
    const { selectionActions, FilterActions: FilterSelectionActions } = useMessageItemActions();

    const { mutate: messageItemUpdate} = useUpdateMessageCentre();

    const UpdateMessageThreadState = (value: boolean) => {
        setMessageThreadOpen(value);
        addGlobalDocumentValue('message-thread-open', value);
    }

    const DeleteMessage = () => {
        const messageIds = selectedItems.length > 0 ? selectedItems : [parseInt(urlParams.messageId as string)];

        deleteMessages(messageIds)
            .then((r) => {
                if(r.status === 200) {
                    messageItemUpdate({
                        newData: {
                            messageIds: messageIds,
                            folderName: 'all'
                         },
                        updateType: MessageItemUpdateType.DELETE,
                        skipItemUpdate: false
                    });

                    if(urlParams.messageId && messageIds.includes(parseInt(urlParams.messageId))) {
                        navigate('/message-centre', { replace: true });
                    }
                }
                setMessageUpdateStatus((prevState) => {
                    return {
                        ...prevState,
                        updating: false,
                    }
                });
                ResetSelection();
                addNotification({ title: `Deleted ${messageIds.length}`, icon: faGalaxy, colour: ColourSolid.Success });
            })
            .catch((e) => {
                setMessageUpdateStatus((prevState) => {
                    return {
                        ...prevState,
                        updating: false,
                    }
                });

                ResetSelection();
                addNotification({ title: 'Deleting Messages Failed', description: e.data?.error, manualClose: true, icon: faGalaxy, colour: ColourSolid.Alert });
                console.log('DeleteMessage ex', e);
            })
    };

    const ArchiveMessage = (archive: boolean) => {
        const messageIds = selectedItems.length > 0 ? selectedItems : [parseInt(urlParams.messageId as string)];

        archiveMessages(messageIds, archive)
            .then((r) => {
                if(r.status === 200) {
                    messageItemUpdate({
                        newData: {
                            messageIds: messageIds,
                            folderName: 'all',
                            isArchived: archive,
                        },
                        updateType: MessageItemUpdateType.ARCHIVE,
                        skipItemUpdate: false
                    });
                }

                setMessageUpdateStatus((prevState) => {
                    return {
                        ...prevState,
                        updating: false,
                    }
                });
                ResetSelection();
                addNotification({ title: archive ? `Archived ${messageIds.length}` : `Un-archived ${messageIds.length}`, icon: faGalaxy, colour: ColourSolid.Success });
            })
            .catch((e) => {
                setMessageUpdateStatus((prevState) => {
                    return {
                        ...prevState,
                        updating: false,
                    }
                });
                ResetSelection();
                addNotification({ title: 'Change Archive Status Failure', description: e.data?.error, manualClose: true, icon: faGalaxy, colour: ColourSolid.Alert });
                console.log('ArchiveMessage ex', e);
            })
    };

    const HandleAction = (actionId: number) => {
        setMessageUpdateStatus((prevState) => {
            return {
                ...prevState,
                updating: true,
            }
        });

        switch(actionId) {
            case 0:
                ArchiveMessage(true);
                break;
            case 1:
                ArchiveMessage(false);
                break;
            case 2:
                DeleteMessage();
                break;
        }
    }

    const UpdateMessageStatus = (updating: boolean, messageId?: number) => {
        setMessageUpdateStatus({
            updating,
            messageId
        })
    }

    const ShowFilters = () => {
        setShowFilterModal(true);
    }

    const SetFilters = (filters: FilterableMessageCentreUrlSlugs) => {
        const newFilters = UpdateAllFilters(filters);
        setUserFilters(filters);
        setParsedFilters({...privateFilters, ...newFilters});
    }

    const RemoveFilter = (filterName: keyof FilterableMessageCentreUrlSlugs) => {
        const newFilters = DeleteFilter(userFilters, filterName);
        setUserFilters(newFilters);
        setParsedFilters({...privateFilters, ...newFilters});
    }

    const ClearFilters = () => {
        setUserFilters({});
        setParsedFilters({...privateFilters});
    }

    const UpdateAllFiltersAndParams = () => {
        const newFilterable = MessageCentreFilterUrlMapping(params, location);
        const newPrivate = MessageCentreUrlMapping(params, location);

        setUrlParams(params);
        setUserFilters(newFilterable);
        setPrivateFilters(newPrivate);
        setParsedFilters({...newPrivate, ...newFilterable});
    }

    useEffect(() => {
        if(messageCentreData) {
            setMessageCentreItemCounts(GetItemCounts(messageCentreData));
        }
    }, [messageCentreData, messageCentreDataUpdatedAt]);

    useEffect(() => {
        if(messageResultsData) {
            setMessageResultsItemCounts(GetItemCounts(messageResultsData));
            setFirstResultThreadId(messageResultsData.pages[0].totalRecords > 0 ? messageResultsData.pages[0].data[0].threadId : -1);
        }
    }, [messageResultsData, messageResultsDataUpdatedAt]);

    useEffect(() => {
        if(firstResultThreadId !== -1 && document.querySelector(`[data-thread-id='${firstResultThreadId}']`)) {
            ManualEventOnly('click', document.querySelector(`[data-thread-id='${firstResultThreadId}']`));
        } else if(messageResultsData) {
            navigate('/message-centre');
        }
    }, [firstResultThreadId, messageResultsIsSuccess]);

    useEffect(() => {
        if(!params.messageId && location.state?.messageId) {
            UpdateAllFiltersAndParams();
        }
    }, [params, location]);

    const messageCentreMemo = useMemo(() => {
        return {
            messageId,
            allSelected,
            visibleSelected,
            visibleIndeterminate,
            selectedItems,
            selectedItemsRef,

            messageCentreData,
            messageCentreError,
            messageCentreIsError,
            messageCentreIsLoading,
            messageCentreIsSuccess,
            messageCentreIsFetchingNextPage,
            messageCentreFetchNextPage,
            messageCentreHasNextPage,
            messageCentreDataUpdatedAt,

            messageCentreItemCounts,

            messageResultsData,
            messageResultsError,
            messageResultsIsError,
            messageResultsIsLoading,
            messageResultsIsSuccess,
            messageResultsIsFetchingNextPage,
            messageResultsFetchNextPage,
            messageResultsHasNextPage,
            messageResultsDataUpdatedAt,

            messageResultsItemCounts,

            userFilters,
            privateFilters,
            parsedFilters,

            messageUpdateStatus,

            singleActions,
            selectionActions,

            messageThreadOpen,

            UpdateMessageThreadState,

            FilterSelectionActions,
            FilterSingleActions,

            HandleAction,

            ToggleAll,
            ToggleVisible,
            ToggleSelected,
            ResetSelection,

            ShowFilters,
            ClearFilters,
            RemoveFilter,
            SetFilters,
            UpdateMessageStatus
        }
    }, [
        messageId,
        allSelected,
        visibleSelected,
        visibleIndeterminate,
        selectedItems,
        selectedItemsRef,

        messageCentreData,
        messageCentreError,
        messageCentreIsError,
        messageCentreIsLoading,
        messageCentreIsSuccess,
        messageCentreIsFetchingNextPage,
        messageCentreFetchNextPage,
        messageCentreHasNextPage,
        messageCentreDataUpdatedAt,

        messageCentreItemCounts,

        messageResultsData,
        messageResultsError,
        messageResultsIsError,
        messageResultsIsLoading,
        messageResultsIsSuccess,
        messageResultsIsFetchingNextPage,
        messageResultsFetchNextPage,
        messageResultsHasNextPage,
        messageResultsDataUpdatedAt,

        messageResultsItemCounts,

        messageThreadOpen,

        userFilters,
        privateFilters,
        parsedFilters,

        messageUpdateStatus,

        singleActions,
        selectionActions,
    ]);

    return (
        <MessageCentreContext.Provider value={messageCentreMemo}>
            {props.children}

            <FilterMessagesModal
                isOpen={showFilterModal}
                closeCallback={() => setShowFilterModal(false)}
            />
        </MessageCentreContext.Provider>
    )
}

const useMessageCentre = () => useContext(MessageCentreContext);

export { MessageCentreContext, useMessageCentre };

export default MessageCentreProvider
